import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, InputAdornment, TextField, Typography, useTheme } from '@mui/material';

import CommonButton from '../CommonButton';
import { CreateUnstakinguser, isCompleteModalType, PoolListType } from '@/types';
import { useWeb3 } from '@/hooks/use-web3';
import { RES_OVER_BLOCK, RES_SUCCESS, STAKING_USER_STATE, UNSTAKETOKEN } from '@/lib/common/name-space';
import { toast } from 'react-toastify';
// asset
import SymbolSvg from '@/assets/assetfi/Symbol';
import CloseSvg from '@/assets/assetfi/Close';
import CheckBoxSvg from '@/assets/assetfi/CheckBox';
import CheckBoxFilledSvg from '@/assets/assetfi/CheckBoxFilled';
import { cutCommas, removeZeroStart } from '@/lib/util';

import { useNavigate } from 'react-router-dom';
import BigNumber from 'bignumber.js';
import Loading from '@/component/mui/Backdrop';
import { setTicker } from '@/component/balance/ticker';
import { checkUnstake, detailStakingInfo, userUnstaking } from '@/api/staking/read';
import { DetailStakingInfoType, PossibleUnstakingAmtType, ResPossibleUnstakingAmtType } from '@/types/staking/read';
import { unstakingUsers } from '@/api/staking/create';
import { addLocalSetStorageArr } from '@/lib/storage';
import ResultPopup from '@/lib/ResultPopup';
import { STATE_STAKE } from '@/lib/common/name-space/status';
import { packBigNumbers } from '@/lib/BigNumbers';
import { useWeb3AuthConnect } from '@web3auth/modal/react';

interface UnstakingModalPropsType {
  handleClose: () => void;
  open: boolean;
  contractAddr: string;
  joinId: string;
  detail: PoolListType;
  stakeIndex: number;
}

const UnstakingModal = ({ handleClose, open, contractAddr, joinId, detail, stakeIndex }: UnstakingModalPropsType) => {
  // state
  const [amount, setAmount] = useState('0');
  const [showAmount, setShowAmount] = useState('0');
  const [usdt, setUsdt] = useState('0');
  const [checked, setChecked] = useState(false);
  const [isCompleteModal, setIsCompleteModal] = useState<isCompleteModalType>({
    success: false,
    fail: false,
    pending: false,
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [stakeInfo, setStakeInfo] = useState<DetailStakingInfoType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // const [stakeInfo, setStakeInfo] = useState<UnstakingInfo | null>(null);
  const address = useRef('');
  const [possible, setPossible] = useState<ResPossibleUnstakingAmtType | null>(null);

  const theme = useTheme();
  const navigate = useNavigate();
  const { web3authCall, address: addr } = useWeb3();
  const { isConnected } = useWeb3AuthConnect();
  const [isRetry, setIsRetry] = useState(false); // 50블록 넘었을 때 다시 시도

  useEffect(() => {
    (async () => {
      if (isConnected && addr) {
        address.current = addr;
      }
    })();

    return () => {};
  }, [isConnected, addr]);

  useEffect(() => {
    if (joinId && isConnected && addr) {
      (async () => {
        address.current = addr;

        const info = await detailStakingInfo(joinId);
        console.log(info);
        const obj: PossibleUnstakingAmtType = {
          poolId: detail.poolId,
          address: addr,
          stakeIndex: info.data.stakeIndex,
        };
        const re = await userUnstaking(obj);
        setPossible(re.data[0]);
        setStakeInfo(info.data);
        // const arrPromise = [];
        // arrPromise.push(() => web3authCall.callStaking(GETPOOLDETAILS, contractAddr));
        // arrPromise.push(() => web3authCall.callStaking(GETSTAKE, contractAddr, [address.current, stakeIndex]));
        // try {
        //   const result = await promiseAllSettled(arrPromise);
        //   const obj = {
        //     ...result[0],
        //     ...result[1],
        //   };
        //   setStakeInfo(obj);
        // } catch (e) {
        //   console.error(e);
        // }
      })();
    }

    return () => {};
  }, [contractAddr, joinId, isConnected, addr]);

  useEffect(() => {
    (async () => {
      if (open) {
        console.log(`open`);
        setUsdt(await setTicker({ navigate: navigate }));
      }
    })();

    return () => {};
  }, [open]);

  // 50블록 안에 채택이 되지 않을 시 다시 시도할 때
  useEffect(() => {
    if (isRetry) {
      (async () => {
        await handleConfirm();
        setIsRetry(false);
      })();
    }

    return () => {};
  }, [isRetry]);

  // method
  const handleConfirm = async () => {
    if (!amount) {
      toast.error('Please enter the quantity');
      return;
    }
    if (!address.current) {
      toast.error('Please check the wallet connection.');
      return;
    }
    if (!stakeInfo) {
      toast.error('Please check the pool information.');
      return;
    }

    if (BigNumber(amount).gt(stakeInfo.totStakeAmt)) {
      toast.error('Please check the unstaking amount.');
      return;
    }
    handleClose();
    setIsLoading(true);
    const obj: CreateUnstakinguser = {
      poolId: stakeInfo.poolId,
      address: stakeInfo.address,
      stakeAmt: amount,
      hash: '',
      ticker: stakeInfo.ticker,
      stakeState: STAKING_USER_STATE.UNSTAKING,
      joinId: stakeInfo.joinId,
      stakeIndex: stakeInfo.stakeIndex,
      usdt: stakeInfo.usdt,
    };

    try {
      // 예외처리 진행
      await checkUnstake(obj);

      // 실제 트랜젝션 처리
      const action = await unStakeToken();
      if (action.code === RES_OVER_BLOCK) {
        triggerIsCompleteModal('pending');
        setErrorMsg(action.msg);
        return;
      } else if (action.code !== RES_SUCCESS) {
        triggerIsCompleteModal('fail');
        setErrorMsg(action.msg);
        return;
      }
      obj.hash = action.data.transactionHash;

      // 디비처리
      try {
        await unstakingUsers(obj, false);
      } catch (e) {
        addLocalSetStorageArr('unstake', obj);
        console.log(e);
      }

      triggerIsCompleteModal('success');
      setTimeout(() => {
        navigate('/portfolio');
      }, 5000);
    } catch (e: any) {
      console.error(e);
      triggerIsCompleteModal('fail');
      setErrorMsg(e?.message || JSON.stringify(e));
    } finally {
      setIsLoading(false);
    }
  };
  const unStakeToken = async () => {
    const payload = [address.current, String(stakeIndex), amount];
    return await web3authCall!.callStaking(UNSTAKETOKEN, contractAddr, payload);
  };
  const triggerIsCompleteModal = (key: keyof isCompleteModalType) => {
    setIsCompleteModal((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: '320px',
            padding: '24px 0px',
            borderRadius: '20px',
          },
        }}
        open={open}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            padding: '0px 24px',
          }}
        >
          <SymbolSvg width={24} height={24} />
          <Typography color={theme.greyscale.darkgrey2} variant="h6">
            {detail.title}
          </Typography>

          <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
            <CloseSvg width={24} height={24} />
          </Box>
        </Box>
        <Box sx={{ marginTop: '24px', padding: '0px 16px', position: 'relative' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Stake Amount
          </Typography>

          <TextField
            value={showAmount}
            onChange={(e) => {
              const deleteCommaValue = cutCommas(e.target.value);
              if (!e.target.value) {
                setAmount('0');
                setShowAmount('0');
                return;
              }
              setAmount(removeZeroStart(deleteCommaValue));
              setShowAmount(removeZeroStart(packBigNumbers(deleteCommaValue)));
            }}
            placeholder="0"
            sx={{
              width: '100%',
              marginTop: '8px',

              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '& input': {
                  textAlign: 'right',
                  typography: 'h4',
                  color: theme.greyscale.darkgrey2,
                  padding: '8px',
                  paddingRight: '0px',
                  paddingBottom: '28px',
                },
                '& fieldset': {
                  borderColor: theme.background.blue,
                },
                '&:hover fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.MuiInputBase-root': {
                  background: '#357AFF1A',
                  padding: 0,
                },
                '& .MuiTypography-root': {
                  typography: 'h5',
                  color: theme.greyscale.darkgrey2,
                },
              },
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  sx={{
                    fontSize: '24px',
                    paddingRight: '8px',
                    paddingBottom: '20px',
                  }}
                  position="end"
                >
                  SFL
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ position: 'absolute', right: 24, bottom: 8 }} color={theme.greyscale.grey3} variant="body2">
            ~ {BigNumber(amount).multipliedBy(usdt).toFixed()} USD
          </Typography>
        </Box>
        {/* <Box
          sx={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            padding: '0 16px',
          }}
        >
          <InfoSvg width={14} height={14} />
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '18px',
              fontWeight: 400,
              letterSpacing: '-0.05em',
            }}
            variant="subtitle2"
            color={theme.background.red}
          >
            The Market Price At The Time Of Unstaking Is Reflected.
          </Typography>
        </Box> */}
        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Stake Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {amount}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    SFL
                  </Typography>
                </Typography>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  ~ {BigNumber(amount).multipliedBy(usdt).toFixed()}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Possible Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {/* {stakeInfo && stakeInfo.amountStaked} */}
                  {possible?.totStakeAmt} SFL
                </Typography>
              </Box>
            </Box>
            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Duration
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  1 Year
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Minimum Deposit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  0{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Total Rewards
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  display={'inline'}
                  sx={{
                    textDecoration: 'line-through',
                  }}
                  variant="subtitle2"
                  color={theme.greyscale.darkgrey2}
                >
                  100{' '}
                </Typography>
                <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                  USD
                </Typography>
              </Box>
            </Box> */}
          </Box>
        </Box>
        <Box
          sx={{
            padding: '24px',
            borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ cursor: 'pointer' }} onClick={() => setChecked((prev) => !prev)}>
            {checked ? <CheckBoxFilledSvg width={20} height={20} /> : <CheckBoxSvg width={20} height={20} />}
          </Box>

          <Typography
            sx={{
              fontWeight: '400',
              fontSize: '13px',
              lineHeight: '15.73px',
              letterSpacing: '-0.02em',
            }}
            color={theme.greyscale.darkgrey1}
          >
            No rewards are credited for early withdrawal, and commission is required
          </Typography>
        </Box>
        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          {checked === true ? (
            <CommonButton
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={checked ? '#ffffff' : theme.background.blue}
              bgColor={checked ? theme.background.blue : '#ffffff'}
              border={theme.background.blue}
            />
          ) : (
            <CommonButton
              disabled
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={checked ? '#ffffff' : theme.background.blue}
              bgColor={checked ? theme.background.blue : '#ffffff'}
              border={theme.background.blue}
            />
          )}
          <CommonButton
            sx={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: '10px',
              marginTop: '10px',
            }}
            text="Back"
            handleClick={handleClose}
            variant="h6"
            color={theme.greyscale.lightgrey2}
            border={theme.greyscale.lightgrey2}
            bgColor={'#ffffff'}
          />
        </Box>
      </Dialog>
      <ResultPopup
        isCompleteModal={isCompleteModal}
        triggerIsCompleteModal={triggerIsCompleteModal}
        errorMsg={errorMsg}
        type={STATE_STAKE.UNSTAKE}
        setIsRetry={setIsRetry}
      />

      <Loading isLoading={isLoading} />
    </>
  );
};

export default UnstakingModal;

